/**
 * Pure queries over the business `LayoutSchema` (架构方案 §5.7): the
 * `enabled` / `visible` policy per area, plus occupancy overlays
 * ({@link withAreaOccupied}). Kept separate from the renderer-owned
 * structural specs (`area-specs.ts`) so an effective layout is a simple merge.
 */
import type { LayoutArea, LayoutAreaConfig, LayoutSchema } from '@hibro/shared';

export function findAreaConfig(
  schema: LayoutSchema,
  area: LayoutArea,
): LayoutAreaConfig | undefined {
  return schema.areas.find((c) => c.area === area);
}

/** Whether the area exists in this build (`enabled !== false`, default true). */
export function isAreaEnabled(config: LayoutAreaConfig | undefined): boolean {
  return config ? config.enabled !== false : false;
}

/** Whether the area is shown by default (`visible !== false`, default true). */
export function isAreaVisible(config: LayoutAreaConfig | undefined): boolean {
  return config ? config.visible !== false : false;
}

/**
 * Overlay occupancy onto a schema area: the area stays enabled only when the
 * base schema enables it *and* some contributed view occupies it. Unoccupied
 * areas become `enabled: false` (never created; their toggle is hidden) without
 * mutating the input. A schema that already disabled the area stays disabled
 * even when occupied — occupancy cannot revive a build that opted the area out.
 */
export function withAreaOccupied(
  schema: LayoutSchema,
  area: LayoutArea,
  occupied: boolean,
): LayoutSchema {
  return {
    areas: schema.areas.map((config) =>
      config.area === area
        ? { ...config, enabled: isAreaEnabled(config) && occupied }
        : config,
    ),
  };
}
